import { useEffect, useState } from 'react';
import { supabase } from '@mondivago/supabase';
import { useSearchParams } from 'react-router-dom';
import { logger } from '@/shared/lib/logger';
import { getOrCreateSessionId } from '@/hooks/analytics';

/**
 * usePartnerTracking
 *
 * Tracks partner referrals via URL parameter `?ref=CODE`.
 * On mount, checks the URL for a `ref` param, resolves it to a partner_id
 * via a Supabase RPC, stores it in localStorage with a 14-day expiry, and
 * records a `referral_click` analytics event so partner link performance
 * (clicks → conversions) is tracked in `app_analytics_events`.
 */

const STORAGE_KEY = 'mondivago_partner';
const EXPIRY_DAYS = 14;

interface StoredPartner {
  partnerId: string;
  expiresAt: number;
}

export function usePartnerTracking() {
  const [searchParams] = useSearchParams();
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    const refCode = searchParams.get('ref');

    if (!refCode) {
      setResolved(true);
      return;
    }

    const stored = getStoredPartnerData();
    // If we already have this partner cached in the window, skip re-resolving
    // but still record the referral click for this visit.
    if (stored && !isExpired(stored)) {
      recordReferralClick(refCode, stored.partnerId);
      setResolved(true);
      return;
    }

    const resolveRef = async () => {
      let partnerId: string | null = null;
      try {
        const { data, error } = await supabase.rpc('resolve_partner_ref_code', {
          p_ref_code: refCode,
        });

        if (error) {
          logger.warn('[PartnerTracking] Failed to resolve ref code:', error);
        } else if (data) {
          partnerId = data as string;
          const expiresAt = Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
          const storedData: StoredPartner = { partnerId, expiresAt };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(storedData));
        }
      } catch (err) {
        logger.warn('[PartnerTracking] Error:', err);
      }

      recordReferralClick(refCode, partnerId);
      setResolved(true);
    };

    void resolveRef();
  }, [searchParams]);

  return { resolved };
}

export function getStoredPartnerId(): string | null {
  const stored = getStoredPartnerData();
  if (!stored || isExpired(stored)) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return stored.partnerId;
}

function getStoredPartnerData(): StoredPartner | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredPartner;
  } catch {
    return null;
  }
}

function isExpired(stored: StoredPartner): boolean {
  return Date.now() > stored.expiresAt;
}

/**
 * Records a referral click into app_analytics_events (fire-and-forget).
 * Duplicate clicks within the same session are de-duplicated at query time,
 * so the same device/day visiting repeatedly only counts once.
 */
function recordReferralClick(refCode: string, partnerId: string | null): void {
  const targetUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';
  void supabase
    .from('app_analytics_events')
    .insert({
      event_type: 'referral_click',
      tour_id: null,
      session_id: getOrCreateSessionId(),
      user_id: null,
      metadata: {
        ref_code: refCode,
        partner_id: partnerId,
        target_url: targetUrl,
      },
    })
    .then(({ error }) => {
      if (error) {
        logger.warn('[PartnerTracking] Failed to log referral click:', error.message);
      }
    });
}
